import { queryElement } from "@modules/bookmarks/html-elements"
import { htmlToElement } from "@modules/bookmarks/html-parser"
import { MessageId, getMessageTemplateById } from "@modules/bookmarks/messages"
import { type TagInstance, makeDefaultTag, makeTag } from "@modules/bookmarks/tags/Tag"
import { registerDropdownEvents } from "@modules/bookmarks/tags/options-dropdown"
import Modals from "@modules/bookmarks/tags/tag-modals"
import { addTagForUser, deleteTagOfUser, fetchTagsFromApi, renameTagForUser } from "@modules/bookmarks/tags/tags-api"
import { InfoPanel } from "@modules/info-panel/InfoPanel"
import { run } from "@modules/util/service"
import { slugifyUnicodeText } from "@modules/util/string-transcoder"

import type { TagData } from "@modules/bookmarks/tags/tags-api"
import type { ApiError } from "@modules/global/ApiError"
import type { PanelType } from "@modules/info-panel/InfoPanel"

interface UIResult {
    inlineError?: HTMLElement | null
    message?: string
    type?: PanelType
    actionSucceeded: boolean
}

export type TagsListData = {
    tagName: string
    tagSlug: string
}

export type RenamedTagsListDataDetail = {
    oldTag: TagsListData
    newTag: TagsListData
}

export const TagsUpdateEvent = "bookmarks.tags:update" as const
export const TagShowNewEvent = "bookmarks.tag:show-add" as const
export const TagAddSuccessEvent = "bookmarks.tag:add-success" as const
export const TagDeleteSuccessEvent = "bookmarks.tag:delete-success" as const
export const TagRenameSuccessEvent = "bookmarks.tag:rename-success" as const

export let tagsList: TagsListData[] = []

const tagAddError = (message: string): HTMLLIElement =>
    htmlToElement<HTMLLIElement>(`
    <div class="tag-add-error">
        <svg viewBox="0 0 20 20">
            <path d="M3.053 17.193c-1.884-1.821-3.053-4.37-3.053-7.193 0-5.523 4.477-10 10-10 2.823 0 5.372 1.169 7.19 3.050l0.003 0.003c1.737 1.796 2.807 4.247 2.807 6.947 0 5.523-4.477 10-10 10-2.7 0-5.151-1.070-6.95-2.81l0.003 0.003zM11.523 10.123l2.83-2.83-1.41-1.41-2.82 2.83-2.83-2.83-1.41 1.41 2.83 2.83-2.83 2.83 1.41 1.41 2.83-2.83 2.83 2.83 1.41-1.41-2.83-2.83z"></path>
        </svg>
        <span class="message">${message}</span>
    </div>
`)

export const tagList: HTMLUListElement | null = queryElement<HTMLUListElement>("#tags-list")

export const tagAddOpenButton = (): HTMLButtonElement | null =>
    queryElement<HTMLButtonElement>(".bookmarks-container .tags lds-button")
const tagAddInput = (): HTMLInputElement | null => {
    const root = Modals.tagAddModal()

    if (root === null) {
        return null
    }

    return queryElement<HTMLInputElement>("#new-tag-input", root)
}
const tagRenameInput = (): HTMLInputElement | null => {
    const root = Modals.tagRenameModal()

    if (root === null) {
        return null
    }

    return queryElement<HTMLInputElement>("#rename-tag-input", root)
}

let defaultTag: TagInstance | null = null

const addTagsToTagList = (tagEntries: TagData[]): void => {
    tagsList = []

    for (const tagData of tagEntries) {
        tagsList.push({ tagName: tagData.tagName, tagSlug: tagData.tagSlug })
        makeTag(tagData.tagName, tagData.tagSlug).addToList(tagList, defaultTag)
    }

    if (tagEntries.length !== 0) {
        tagsList.push({ tagName: "Ohne Tag", tagSlug: "-" })
    }
}

const saveNewTag = (): void => {
    const newTagNameInput: HTMLInputElement | null = tagAddInput()

    if (newTagNameInput === null) {
        return
    }

    const tagAddErrorElement: HTMLElement | null = Modals.tagAddModal()?.querySelector(".tag-add-error") || null
    tagAddErrorElement?.parentElement?.removeChild(tagAddErrorElement)

    const tagName: string = newTagNameInput.value as string
    const tagSlug: string = slugifyUnicodeText(tagName)

    const uiResult: UIResult = { actionSucceeded: false }

    addTagForUser(userData.userId, tagName, tagSlug)
        .then((): void => {
            uiResult.message = getMessageTemplateById(MessageId.TagAddSuccess)(tagName)
            uiResult.actionSucceeded = true
            newTagNameInput.value = ""
        })
        .catch((error: ApiError): void => {
            error.throwIfIsNot(MessageId.TagAdd_AlreadyExistsError)
            uiResult.inlineError = tagAddError(getMessageTemplateById(MessageId.TagAdd_AlreadyExistsError)())
        })
        .catch((error: ApiError): void => {
            error.throwIfIsNot(MessageId.TagAdd_Error)
            uiResult.message = getMessageTemplateById(MessageId.TagAdd_Error)()
            uiResult.type = InfoPanel.Type.Error
        })
        .catch((error: ApiError): void => {
            error.throwIfIsNot(MessageId.TagAdd_Limit_Error)
            uiResult.message = getMessageTemplateById(MessageId.TagAdd_Limit_Error)()
            uiResult.type = InfoPanel.Type.Error
        })
        .catch((): void => {
            uiResult.message = "Ein unbekannter Fehler ist aufgetreten"
            uiResult.type = InfoPanel.Type.Error
        })
        .finally((): void => {
            if (uiResult.message && uiResult.message !== "") {
                InfoPanel.displayMessage(uiResult.message, uiResult.type || "")
            }
            if (uiResult.inlineError) {
                newTagNameInput.parentElement?.appendChild(uiResult.inlineError)
            }
            if (uiResult.actionSucceeded) {
                tagsList.push({ tagName, tagSlug })
                makeTag(tagName, tagSlug).addToList(tagList, defaultTag)

                document.dispatchEvent(
                    new CustomEvent(TagAddSuccessEvent, {
                        detail: { tagName, tagSlug },
                    }),
                )

                Modals.closeTagAddModal()
                updateTagList()
                disableTagAddButtonWhenLimitReached()
            }
        })
}

const deleteTag = (): void => {
    const tag: TagInstance | null = Modals.tagDeleteModal()?.tag ?? null

    if (tag === null) {
        return
    }

    const tagSlug: string = tag.tagSlug
    const tagName: string = tag.tagName

    const uiResult: UIResult = { actionSucceeded: false }

    deleteTagOfUser(userData.userId, tagSlug)
        .then((): void => {
            uiResult.actionSucceeded = true
            uiResult.message = getMessageTemplateById(MessageId.TagDeleteSuccess)(tagName)
        })
        .catch((error: ApiError): void => {
            error.throwIfIsNot(MessageId.TagDelete_Error)
            uiResult.message = getMessageTemplateById(MessageId.TagDelete_Error)(tagName)
            uiResult.type = InfoPanel.Type.Error
        })
        .catch((): void => {
            uiResult.message = "Ein unbekannter Fehler ist aufgetreten"
            uiResult.type = InfoPanel.Type.Error
        })
        .finally((): void => {
            if (uiResult.message && uiResult.message !== "") {
                InfoPanel.displayMessage(uiResult.message, uiResult.type || "")
            }
            if (uiResult.actionSucceeded) {
                tagsList = tagsList.filter((tagData) => tagData.tagSlug !== tagSlug)
                tag.removeFromList()

                document.dispatchEvent(
                    new CustomEvent(TagDeleteSuccessEvent, {
                        detail: { tagName, tagSlug },
                    }),
                )

                Modals.closeTagDeleteModal()
                updateTagList()
                disableTagAddButtonWhenLimitReached()
            }
        })
}

const renameTag = (): void => {
    const tag: TagInstance | null = Modals.tagRenameModal()?.tag ?? null

    if (tag === null) {
        return
    }

    const oldTagSlug: string = (tag.tagSlug as string) || ""
    const oldTagName: string = (tag.tagName as string) || ""

    if (oldTagSlug === "") {
        return
    }

    const newTagNameInputElement: HTMLInputElement | null = tagRenameInput()
    const newTagName: string = (newTagNameInputElement?.value || "") as string

    if (newTagName === "") {
        InfoPanel.displayMessage(getMessageTemplateById(MessageId.TagRenameEmptyError)())
        return
    }

    if (oldTagName === newTagName) {
        InfoPanel.displayMessage(getMessageTemplateById(MessageId.TagRenameNoChangeError)())
        return
    }

    const newTagSlug: string = slugifyUnicodeText(newTagName)
    const uiResult: UIResult = { actionSucceeded: false }

    if (newTagName === "Ohne Tag" || newTagSlug === "-") {
        InfoPanel.displayMessage(getMessageTemplateById(MessageId.TagRenameExistsError)(newTagName))
        return
    }

    renameTagForUser(userData.userId, oldTagSlug, newTagName, newTagSlug)
        .then((): void => {
            uiResult.actionSucceeded = true
            uiResult.message = getMessageTemplateById(MessageId.TagRenameSuccess)(oldTagName, newTagName)
        })
        .catch((error: ApiError): void => {
            error.throwIfIsNot(MessageId.TagRenameExistsError)
            uiResult.message = getMessageTemplateById(MessageId.TagRenameExistsError)(oldTagName)
            uiResult.type = InfoPanel.Type.Error
        })
        .catch((): void => {
            uiResult.message = "Ein unbekannter Fehler ist aufgetreten"
            uiResult.type = InfoPanel.Type.Error
        })
        .finally((): void => {
            if (uiResult.message && uiResult.message !== "") {
                InfoPanel.displayMessage(uiResult.message, uiResult.type || "")
            }
            if (uiResult.actionSucceeded) {
                const changedTag = tagsList.find((tagData) => tagData.tagSlug === oldTagSlug)

                if (changedTag !== undefined) {
                    changedTag.tagSlug = newTagSlug
                    changedTag.tagName = newTagName
                }

                tag.tagSlug = newTagSlug
                tag.tagName = newTagName

                document.dispatchEvent(
                    new CustomEvent(TagRenameSuccessEvent, {
                        detail: {
                            oldTag: { tagName: oldTagName, tagSlug: oldTagSlug },
                            newTag: { tagName: newTagName, tagSlug: newTagSlug },
                        },
                    }),
                )

                const tagRenameInputElement = tagRenameInput()
                if (tagRenameInputElement) {
                    tagRenameInputElement.value = ""
                }
                Modals.closeTagRenameModal()
            }
        })
}

function tagListService() {
    return async (): Promise<void> => {
        tagAddOpenButton()?.addEventListener(
            "click",
            (): void => {
                tagList?.dispatchEvent(new CustomEvent(TagShowNewEvent, { bubbles: true }))
            },
            { passive: true },
        )

        Modals.registerModalEvents()

        if (!userData.isLoggedIn) {
            return
        }

        Modals.registerTagAddButtonAction((): void => saveNewTag())
        Modals.registerTagDeleteButtonAction((): void => deleteTag())
        Modals.registerTagRenameButtonAction((): void => renameTag())

        defaultTag = makeDefaultTag()
        defaultTag.addToList(tagList)

        fetchTagsFromApi(userData.userId)
            .then((entries: TagData[]): void => {
                document.dispatchEvent(new CustomEvent(TagsUpdateEvent, { detail: entries }))
                addTagsToTagList(entries)
                updateTagList()
                disableTagAddButtonWhenLimitReached()
            })
            .catch((error: ApiError): void => console.error(error.message))

        registerDropdownEvents()
    }
}

export function updateTagList(): void {
    const noTagsText = document.getElementById("no-tags-text")
    const withoutTag = document.getElementById("without-tag")
    const tagsListElement = document.getElementById("tags-list")

    if (noTagsText && withoutTag && tagsListElement) {
        if (tagsListElement.childElementCount > 2) {
            noTagsText.classList.add("hidden")
            withoutTag.classList.remove("hidden")
        } else {
            noTagsText.classList.remove("hidden")
            withoutTag.classList.add("hidden")
        }
    }
}

export function disableTagAddButtonWhenLimitReached(): void {
    const tagsListElement = document.getElementById("tags-list")

    const tagAddButton = document.querySelector("section.tags > lds-button")?.shadowRoot?.querySelector("button")

    if (tagsListElement && tagAddButton) {
        if (tagsListElement.childElementCount >= 12) {
            tagAddButton.setAttribute("disabled", "")
        } else {
            tagAddButton.removeAttribute("disabled")
        }
    }
}

if (SiteInfo.bookmarksPageActive) {
    run(tagListService())
}

// Register Custom Events
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface DocumentEventMap {
        [TagsUpdateEvent]: CustomEvent<TagData[]>
        [TagShowNewEvent]: CustomEvent
        [TagAddSuccessEvent]: CustomEvent<TagsListData>
        [TagDeleteSuccessEvent]: CustomEvent<TagsListData>
        [TagRenameSuccessEvent]: CustomEvent<RenamedTagsListDataDetail>
    }
    interface HTMLElementEventMap {
        [TagsUpdateEvent]: CustomEvent<TagData[]>
        [TagShowNewEvent]: CustomEvent
        [TagAddSuccessEvent]: CustomEvent<TagsListData>
        [TagDeleteSuccessEvent]: CustomEvent<TagsListData>
        [TagRenameSuccessEvent]: CustomEvent<RenamedTagsListDataDetail>
    }
}
